"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ItemWithPromotionSchema = exports.ItemSchema = exports.BaseItemSchema = exports.ItemOptionSchema = exports.ItemVariantOptionValueSchema = exports.ItemVariantOptionChannelIdSchema = exports.ItemVariantSchema = exports.BaseItemVariantSchema = exports.PicturesSchema = exports.ItemVariantOptionMapSchema = exports.VariantNameSchema = exports.MAX_ITEM_OPTIONS = exports.MAX_ITEM_OPTION_VALUES = void 0;
const zod_1 = require("zod");
const _1 = require(".");
const __1 = require("..");
exports.MAX_ITEM_OPTION_VALUES = 50;
exports.MAX_ITEM_OPTIONS = 2;
exports.VariantNameSchema = zod_1.z.string().min(1).max(32);
exports.ItemVariantOptionMapSchema = zod_1.z.object({
    name: exports.VariantNameSchema,
    value: __1.ShortStringSchema,
});
exports.PicturesSchema = __1.LongStringSchema.url().array();
exports.BaseItemVariantSchema = __1.BaseSchema.extend({
    /**
     * The position can be used to properly order a group of variants.
     */
    position: zod_1.z.number().nonnegative(),
    /**
     * The variant alias, it can be used on the interface to differentiate the variants and identify them by an alias.
     *
     * For example, a t-shirt might have 3 variants that could be aliased as Black, Pink and Red.
     */
    alias: __1.ShortStringSchema.optional(),
    title: __1.ShortStringSchema.optional(),
    description: __1.ExtraLongStringSchema.optional(),
    sku: zod_1.z.string().min(1).max(40).optional(),
    identifierType: zod_1.z.nativeEnum(__1.ItemIdentifierType).optional(),
    identifierValue: __1.ShortStringSchema.min(1).optional(),
    identifiers: zod_1.z.array(_1.ItemIdentifierSchema).optional(),
    pictures: exports.PicturesSchema.optional(),
    /**
     * The total amount of items available to sell of this variant.
     *
     * When the value is set and `isQuantityManagedByOffers` is `false`, the same quantity applies to all offers, and one sell in any channel will affect the quantity available to all of them.
     *
     * When `isQuantityManagedByOffers` is `true`, the quantity available becomes just the sum of quantity available in all orders.
     *
     * @see {@link isQuantityManagedByOffers}
     */
    quantityAvailable: zod_1.z.number().optional(),
    /**
     * The total amount of items ordered by pending payment confirmation of this variant.
     *
     * When the value is set and `isQuantityManagedByOffers` is `false`, the same quantity applies to all offers, and one sell in any channel will affect the quantity available to all of them.
     *
     * When `isQuantityManagedByOffers` is `true`, the quantity available becomes just the sum of quantity available in all orders.
     *
     * @see {@link isQuantityManagedByOffers}
     */
    quantityReserved: zod_1.z.number().optional(),
    /**
     * The total amount of items sold of this variant
     */
    quantitySold: zod_1.z.number().nonnegative().optional(),
    /**
     * The total amount of items of this variant.
     *
     * Basic is the sum of quantityAvailable and quantityReserved.
     *
     * @see {@link quantityAvailable}
     * @see {@link quantityReserved}
     */
    quantityTotal: zod_1.z.number().optional(),
    /**
     * Defines if quantity available and reserverd must be managed at the variant level or by each offer.
     *
     * @defaultValue `false`
     */
    isQuantityManagedByOffers: zod_1.z.boolean(),
    /**
     * The item sale price when it is the same one for all offers.
     *
     * @see {@link isPriceManagedByOffers}
     * @see {@link minimumPrice}
     * @see {@link maximumPrice}
     */
    price: __1.MoneySchema.optional(),
    /**
     * Defines if price must be managed at the variant level or by each offer.
     *
     * @defaultValue `false`
     */
    isPriceManagedByOffers: zod_1.z.boolean(),
    /**
     * The minimum price across all offers, this property is only available when `isPriceManagedByOffers` is set to true.
     *
     * @see {@link isPriceManagedByOffers}
     * @see {@link price}
     */
    minimumPrice: __1.MoneySchema.optional(),
    /**
     * The maximum price across all offers, this property is only available when `isPriceManagedByOffers` is set to true.
     *
     * @see {@link isPriceManagedByOffers}
     * @see {@link price}
     */
    maximumPrice: __1.MoneySchema.optional(),
    /**
     * Flag indicating if the base item has been deleted.
     */
    archived: zod_1.z.boolean().optional(),
    source: zod_1.z.nativeEnum(__1.ItemSource).optional(),
    /**
     * Flag indicating for the create SKU generate from RR or from user
     *
     * Default is from User (false)
     *
     * Change this variable when press the otion in Front-End
     */
    generateSKU: zod_1.z.boolean().optional(),
    /**
     *  The minimum quantity of the item that must be available in stock to be listed on the channel.
     */
    minimumQuantityAvailable: zod_1.z.number().nonnegative().optional(),
    promotionPrice: __1.MoneySchema.optional(),
});
exports.ItemVariantSchema = exports.BaseItemVariantSchema.extend({
    offers: zod_1.z.array(_1.ItemOfferSchema).optional(),
    optionMapping: zod_1.z.array(exports.ItemVariantOptionMapSchema).max(exports.MAX_ITEM_OPTION_VALUES).optional(),
});
exports.ItemVariantOptionChannelIdSchema = zod_1.z.record(zod_1.z.nativeEnum(__1.ChannelIDs), __1.ShortStringSchema.optional());
exports.ItemVariantOptionValueSchema = zod_1.z.object({
    name: exports.VariantNameSchema,
    channelValueIds: exports.ItemVariantOptionChannelIdSchema.optional(),
    pictures: exports.PicturesSchema.optional(),
    position: zod_1.z.number().nonnegative().optional(),
});
exports.ItemOptionSchema = zod_1.z.object({
    position: zod_1.z.number().nonnegative(),
    name: exports.VariantNameSchema,
    channelOptionIds: exports.ItemVariantOptionChannelIdSchema.optional(),
    values: zod_1.z.array(exports.ItemVariantOptionValueSchema).max(exports.MAX_ITEM_OPTION_VALUES),
});
exports.BaseItemSchema = __1.BaseSchema.extend({
    store: __1.BaseStoreSchema.partial().required({ id: true }),
    brand: __1.ShortStringSchema.optional(),
    isNonBranded: zod_1.z.boolean().optional(),
    manufacturer: __1.ShortStringSchema.optional(),
    unity: zod_1.z.nativeEnum(__1.ItemUnity).optional(),
    condition: zod_1.z.nativeEnum(__1.ItemCondition).optional(),
    /**
     * List of attributes that are varied in the item.
     */
    variableAttributes: zod_1.z.array(__1.ShortStringSchema).optional(),
    /**
     * The base variant contains all details for the item.
     */
    baseVariant: exports.BaseItemVariantSchema,
    /**
     * For an item that has variants, this array stores the options for the item, such as color and size.
     *
     * Only the attributes that vary should be populated by the variant, the ones that don't vary must be obtained from the baseVariant.
     */
    additionalVariants: zod_1.z.array(exports.BaseItemVariantSchema),
    /**
     * The total amount of items available to sell of all variants of this item.
     *
     * @see {@link isQuantityManagedByOffers}
     */
    quantityAvailable: zod_1.z.number().optional(),
    /**
     * The total amount of items ordered by pending payment confirmation of all variants of this item.
     *
     * @see {@link isQuantityManagedByOffers}
     */
    quantityReserved: zod_1.z.number().optional(),
    /**
     * The total amount of items sold of all variant this item
     */
    quantitySold: zod_1.z.number().nonnegative().optional(),
    /**
     * The total amount of items for all variants.
     *
     * The sum of quantityAvailable and quantityReserved.
     *
     * @see {@link quantityAvailable}
     * @see {@link quantityReserved}
     */
    quantityTotal: zod_1.z.number().optional(),
    /**
     * Flag indicating if the base item has been deleted.
     */
    archived: zod_1.z.boolean().optional(),
    options: zod_1.z.array(exports.ItemOptionSchema).max(exports.MAX_ITEM_OPTIONS).optional(),
    pictures: exports.PicturesSchema,
    /**
     * When user update the item
     */
    userUpdatedAt: zod_1.z.coerce.date().optional(),
    /**
     * The item cost price
     */
    costPrice: __1.MoneySchema.optional(),
    /**
     * The item profit (item price over cost price)
     */
    profit: __1.MoneySchema.extend({ percentage: zod_1.z.number() }).optional(),
    /**
     * The item dimensions. length, width, height and weight
     */
    dimensions: __1.DimensionSchema.optional(),
});
exports.ItemSchema = exports.BaseItemSchema.extend({
    /**
     * The base variant contains all details for the item.
     */
    baseVariant: exports.ItemVariantSchema,
    /**
     * For an item that has variants, this array stores the options for the item, such as color and size.
     *
     * Only the attributes that vary should be populated by the variant, the ones that don't vary must be obtained from the baseVariant.
     */
    additionalVariants: zod_1.z.array(exports.ItemVariantSchema),
    category: _1.ItemCategorySchema.optional(),
    attributeSettings: zod_1.z.array(_1.ItemAttributeSettingDefinitionSchema).optional(),
    /**
     * Spotlight settings
     */
    spotlightPreferences: zod_1.z
        .object({
        enabled: zod_1.z.boolean().optional(),
        position: zod_1.z.number().nonnegative().int().optional(),
    })
        .optional(),
});
exports.ItemWithPromotionSchema = exports.ItemSchema.extend({
    promotion: zod_1.z.lazy(() => __1.PromotionSchema.partial().required({ id: true }).omit({ ads: true, items: true })).optional(),
});
//# sourceMappingURL=item.js.map